import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useGate } from '../../context/GateContext';
import { cn } from '../../lib/utils';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiX, FiLock, FiLogOut, FiLayout, FiUnlock } = FiIcons;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { lockSection, getUnlockedSection } = useGate();

  const unlockedSectionName = getUnlockedSection();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Luka', path: '/section/luka' },
    { name: 'Klara', path: '/section/klara' },
    { name: 'Family', path: '/section/family' },
    { name: 'Trips', path: '/section/trips' },
  ];

  const handleSectionLogout = () => {
    lockSection('all');
    navigate('/');
    setIsOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-[#fdfcf9]/80 backdrop-blur-md border-b border-[#e5e0d5]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center py-4">
          <Link to="/" className="text-xl font-serif font-bold tracking-tight text-[#2c2c2c]">
            Dimovski Family <span className="text-[#8c7851] italic font-medium">Archive</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={cn(
                  "text-sm font-semibold transition-colors hover:text-[#8c7851]",
                  location.pathname === link.path ? "text-[#8c7851]" : "text-[#6b6b6b]"
                )}
              >
                {link.name}
              </Link>
            ))}

            <div className="h-4 w-px bg-stone-200 mx-2" />

            {/* SECTION LOGOUT */}
            {!user && unlockedSectionName && (
              <button 
                onClick={handleSectionLogout}
                className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-full text-[10px] font-bold text-amber-700 hover:bg-amber-100 transition-all border border-amber-200/50"
              >
                <SafeIcon icon={FiUnlock} className="w-3 h-3" />
                <span>LOCK {unlockedSectionName.toUpperCase()}</span>
              </button>
            )}

            {/* ADMIN ACCESS */}
            {user ? (
              <div className="flex items-center gap-2">
                <Link 
                  to="/admin" 
                  className="flex items-center gap-2 px-4 py-2 bg-[#2c2c2c] rounded-xl text-xs font-bold text-white hover:bg-black transition-all shadow-md"
                >
                  <SafeIcon icon={FiLayout} className="w-3.5 h-3.5" />
                  <span>Dashboard</span>
                </Link>
                <button onClick={signOut} className="p-2 text-red-400 hover:text-red-600 transition-colors" title="Sign Out">
                  <SafeIcon icon={FiLogOut} className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <Link 
                to="/admin/login" 
                className="flex items-center gap-2 px-4 py-2 bg-stone-100 rounded-xl text-[10px] font-bold text-[#6b6b6b] hover:bg-stone-200 transition-all border border-stone-200/50"
              >
                <SafeIcon icon={FiLock} className="w-3.5 h-3.5" />
                <span>PARENT LOGIN</span>
              </Link>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-4">
            {!user && unlockedSectionName && (
               <button onClick={handleSectionLogout} className="p-2 text-amber-600">
                <SafeIcon icon={FiUnlock} className="w-5 h-5" />
              </button>
            )}
            <button onClick={() => setIsOpen(!isOpen)} className="p-2 text-[#2c2c2c]">
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav Overlay */}
      {isOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-[#fdfcf9] border-b border-[#e5e0d5] shadow-xl">
          <div className="px-6 py-8 space-y-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className="block text-xl font-bold text-[#2c2c2c]"
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-6 border-t border-stone-100">
              {unlockedSectionName && !user && (
                <button onClick={handleSectionLogout} className="block w-full text-left text-lg font-bold text-amber-600 mb-4">
                  Lock Archive
                </button>
              )}
              <Link to="/admin/login" onClick={() => setIsOpen(false)} className="block text-lg font-bold text-[#8c7851]">
                {user ? 'Admin Dashboard' : 'Parent Admin Login'}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;